import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import {
  StatisticsResponse,
  StatisticsRow,
} from '../../types/supplierStatistics';
import SmartImage from '../SmartImage';
import StatisticsSizeStock from './StatisticsSizeStock';
import StatisticsSummary from './StatisticsSummary';
import StatisticsChannels from './StatisticsChannels';
import StatisticsTrends from './StatisticsTrends';
import StatisticsTable, {
  money,
  number,
  percent,
  StockStatus,
} from './StatisticsTable';
import { displayTime } from './StatisticsNotice';

function ProductImages({ images, name }: { images: string[]; name: string }) {
  const [selected, setSelected] = useState<string>();
  const current = selected && images.includes(selected) ? selected : images[0];
  return (
    <Box>
      <Box
        sx={{
          height: { xs: 180, md: 210 },
          p: 2,
          bgcolor: '#f8fafc',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <SmartImage
          src={current}
          alt={name}
          loading="eager"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </Box>
      {images.length > 1 && (
        <Stack
          direction="row"
          gap={1}
          sx={{ mt: 1.5, p: 0.5, overflowX: 'auto' }}
          aria-label="Produktbilder"
        >
          {images.map((image, index) => (
            <ButtonBase
              key={image}
              onClick={() => setSelected(image)}
              aria-label={`Visa produktbild ${index + 1}`}
              aria-pressed={current === image}
              sx={{
                width: 46,
                height: 50,
                flexShrink: 0,
                p: 0.5,
                border: 1,
                borderRadius: 1,
                borderColor: current === image ? 'primary.main' : 'divider',
                '&:hover': { borderColor: 'primary.main' },
                '&.Mui-focusVisible': {
                  outline: '3px solid',
                  outlineColor: 'primary.light',
                  outlineOffset: 2,
                },
              }}
            >
              <SmartImage
                src={image}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </ButtonBase>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function StatisticsProductDetail({
  data,
  product,
  params,
  refresh,
  detailHref,
}: {
  data: StatisticsResponse;
  product: StatisticsRow;
  params: Record<string, string | number>;
  refresh: number;
  detailHref: (row: StatisticsRow) => string;
}) {
  const colors = (data.colors || []).map((color) => ({
    ...color,
    imageUrl:
      color.imageUrl || data.media?.[color.attribute1Number]?.[0] || null,
  }));
  const images = Array.from(
    new Set(
      [
        product.imageUrl,
        ...colors.map((color) => color.imageUrl),
        ...Object.values(data.media || {}).flat(),
      ].filter((image): image is string => !!image),
    ),
  );
  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: '280px minmax(0, 1fr)',
            lg: '300px minmax(0, 1fr)',
          },
          gap: 3,
          alignItems: 'start',
          mb: 4,
        }}
      >
        <Paper
          variant="outlined"
          sx={{ p: 2.5, borderRadius: 2.5, order: { xs: 1, md: 0 } }}
        >
          <ProductImages
            images={images}
            name={product.productName || product.productNumber}
          />
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mt: 2 }}
          >
            {product.brandName}
          </Typography>
          <Typography component="h2" variant="h6" fontWeight={700}>
            {product.productName || product.productNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Art.nr {product.productNumber || 'saknas'}
          </Typography>
          <Box
            component="dl"
            sx={{
              m: 0,
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 1,
              '& dt': { color: 'text.secondary', fontSize: 12 },
              '& dd': {
                m: 0,
                fontSize: 14,
                fontWeight: 700,
                textAlign: 'right',
              },
            }}
          >
            <dt>Utpris inkl moms</dt>
            <dd>{money(product.sellingPrice)}</dd>
            <dt>Inpris ex moms</dt>
            <dd>{money(product.costPrice)}</dd>
            <dt>Kalkylmarginal ex moms</dt>
            <dd>{percent(product.calculationMarginPercent)}</dd>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.5 }}
          >
            Aktuella priser. Färger kan ha olika pris.
          </Typography>
        </Paper>
        <Box
          component="section"
          aria-labelledby="product-stock-heading"
          sx={{ minWidth: 0, order: { xs: 0, md: 1 } }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography
                id="product-stock-heading"
                component="h2"
                variant="h5"
                fontWeight={700}
              >
                Lager per storlek
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Tillgängligt nu · gemensamt lager för butik och online
              </Typography>
            </Box>
            <Chip
              label={
                product.stockKnown
                  ? `${number(product.availableStock)} st totalt`
                  : 'Lagerunderlag saknas'
              }
              sx={{
                fontWeight: 700,
                bgcolor:
                  product.stockKnown && product.availableStock > 0
                    ? '#eaf3f0'
                    : '#f1f5f9',
                color:
                  product.stockKnown && product.availableStock > 0
                    ? '#16634e'
                    : 'text.secondary',
              }}
            />
          </Stack>
          <Stack gap={2}>
            {colors.map((color, index) => (
              <StatisticsSizeStock
                key={`${color.attribute1Code}-${index}`}
                color={color}
              />
            ))}
            {!colors.length && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography color="text.secondary">
                  Aktuella färg- och storlekskopplingar saknas.
                </Typography>
              </Paper>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
            sx={{ mt: 2 }}
          >
            <Typography variant="caption" color="text.secondary">
              Lageruppdatering: {displayTime(data.metadata.stockAsOf)}
            </Typography>
            <StockStatus row={product} />
          </Stack>
          <Paper variant="outlined" sx={{ p: 2.5, mt: 3, borderRadius: 2.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'minmax(0, 2fr) minmax(0, 1fr)',
                },
                gap: 4,
              }}
            >
              <StatisticsChannels row={product} />
              <Box
                sx={{
                  borderLeft: { md: 1 },
                  borderColor: { md: 'divider' },
                  pl: { md: 4 },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Periodmarginal · uppskattning
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ my: 0.5 }}>
                  {percent(product.periodMarginEstimatePercent)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Beräknad med aktuellt inköpspris. Historiskt inköpspris
                  saknas. Saknad kostnad eller okänd momsgrund ger inget
                  marginalvärde.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Typography component="h2" variant="h5" fontWeight={700}>
          Försäljning & utveckling
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.metadata.period.start} – {data.metadata.period.end} · Alla
          färger
        </Typography>
      </Stack>
      <StatisticsSummary row={product} />
      <StatisticsTrends params={params} refresh={refresh} />
      <Accordion
        disableGutters
        variant="outlined"
        sx={{ mb: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={700}>
            Jämför färger · försäljning och marginal
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <StatisticsTable rows={colors} level="product" href={detailHref} />
        </AccordionDetails>
      </Accordion>
      <Accordion
        disableGutters
        variant="outlined"
        sx={{ '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={700}>
            Senaste registrerade lagerökningar
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {data.stockHistory?.length ? (
            data.stockHistory.map((entry, index) => (
              <Stack
                key={index}
                direction="row"
                gap={2}
                justifyContent="space-between"
                sx={{
                  py: 1.5,
                  borderBottom:
                    index < (data.stockHistory?.length ?? 0) - 1 ? 1 : 0,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">
                  {displayTime(entry.createdAt)}
                </Typography>
                <Stack direction="row" alignItems="center" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    {number(entry.previousBalance)} → {number(entry.newBalance)}
                  </Typography>
                  <Chip size="small" label={`+${number(entry.increase)} st`} />
                </Stack>
              </Stack>
            ))
          ) : (
            <Typography color="text.secondary">
              Inga registrerade lagerökningar i underlaget.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
}
